import { MouseEvent } from 'react';
import { Divider } from 'antd';
import HtmlParser from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';

import Image from 'global/Image';
import BaseSecuritiesModal, {
  BaseSecuritiesModalProps,
} from 'global/Securities/components/Modal/BaseSecuritiesModal';
import useMultiPortfolioStore from 'lib/stores/multiPorto';
import { getRoundDownValue } from 'features/securities/utils';
import {
  formatCurrency,
  formatShiftedCurrency,
  formatPrice,
} from 'features/securities/withdrawal/utils';

import {
  Title,
  Container,
  InfoContainer,
  InfoTitleContainer,
  BalanceContainer,
  BalanceItem,
  BalanceSettlementContainer,
  BalanceSettlementItem,
  LegendsContainer,
} from './cash-review.styled';

interface BalanceReviewProps extends BaseSecuritiesModalProps {
  onBack: () => void;
}

export default function TransferCashBalanceReview({
  onCancel = () => {},
  onBack,
  ...props
}: Readonly<BalanceReviewProps>) {
  const witdrawableData = useMultiPortfolioStore(
    (s) => s.withDrawableData.data,
  );
  const resetMoveCashForm = useMultiPortfolioStore((s) => s.resetMoveCashForm);
  const resetWithdrawableBalance = useMultiPortfolioStore(
    (s) => s.resetWithdrawable,
  );

  const {
    withdrawable,
    transaction,
    pending,
    leverage_liability,
    net_withdrawable,
  } = witdrawableData.balance;
  const { settlement_schedules = [] } = witdrawableData;
  const { legends = [] } = witdrawableData;

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    resetMoveCashForm();
    resetWithdrawableBalance();
  };

  const getFormattedCurrency = (value: number) =>
    formatCurrency(String(getRoundDownValue(value, 0)));

  const getShiftedCurrency = (value: number) =>
    formatShiftedCurrency(getRoundDownValue(value, 0));

  if (!witdrawableData) return null;

  return (
    <BaseSecuritiesModal
      title={
        <Title>
          <Image
            style={{ cursor: 'pointer' }}
            src="/icon/chevron/left.svg"
            alt=""
            onClick={onBack}
          />
          Transfer Cash
        </Title>
      }
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container onClick={(e) => e.stopPropagation()}>
        <InfoContainer>
          <InfoTitleContainer>
            <h1>Saldo Dapat Ditranser</h1>
            <h6>{getFormattedCurrency(withdrawable)}</h6>
          </InfoTitleContainer>

          <BalanceContainer>
            <BalanceItem>
              Saldo Transaksi
              <p>{getShiftedCurrency(transaction)}</p>
            </BalanceItem>

            <BalanceItem>
              Saldo Tertunda
              <p className="negative">{getShiftedCurrency(pending)}</p>
            </BalanceItem>

            <BalanceItem>
              Kewajiban Leverage
              <p className="negative">
                {getShiftedCurrency(leverage_liability)}
              </p>
            </BalanceItem>

            <Divider />

            <BalanceItem className="bold">
              Saldo Dapat Ditransfer (T+0)
              <p>{getShiftedCurrency(net_withdrawable)}</p>
            </BalanceItem>

            <BalanceSettlementContainer>
              {settlement_schedules.map((item) => (
                <BalanceSettlementItem key={item.title}>
                  <p className="bold">{item.title}</p>
                  <p className="date">{item.formatted_date}</p>
                  <p>
                    {formatPrice(String(getRoundDownValue(item.balance, 0)))}
                  </p>
                </BalanceSettlementItem>
              ))}
            </BalanceSettlementContainer>
          </BalanceContainer>
        </InfoContainer>

        <Divider className="content-divider" />

        <LegendsContainer>
          {legends.map((item) => (
            <p key={`content-item-${item.slice(0, 10)}`}>
              {HtmlParser(sanitizeHtml(item) || '')}
            </p>
          ))}
        </LegendsContainer>
      </Container>
    </BaseSecuritiesModal>
  );
}
